import * as React from "react";
import Dialog from "@mui/material/Dialog";
import { USERS } from "../../config/trainers";
import { formatBookingDate } from "../Calendar";
import { buildApiUrl } from "../../config/api";

const emptyForm = { name: "", surname: "", phone: "", comment: "" };

export default function ModalBooking({ selectedDate, refreshTime, slots, trainer, onBooked }) {
  const trainerName = USERS.find((item) => item.id === trainer)?.label;
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const [activeTime, setActiveTime] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [errors, setErrors] = React.useState({});
  const [submitError, setSubmitError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const submissionLock = React.useRef(false);
  const [cancelLink, setCancelLink] = React.useState("");
  const [copyStatus, setCopyStatus] = React.useState("");
  const [bookingDate, setBookingDate] = React.useState(null);

  const handleOpen = () => {
    setBookingDate(selectedDate);
    setStep(0); setActiveTime(null); setForm(emptyForm); setErrors({});
    setSubmitError(""); setCancelLink(""); setCopyStatus(""); setOpen(true);
  };
  const handleClose = () => {
    if (submissionLock.current) return;
    setOpen(false);
    refreshTime();
  };
  const copyCancelLink = async () => {
    try {
      await navigator.clipboard.writeText(cancelLink);
      setCopyStatus("Посилання скопійовано");
    } catch {
      setCopyStatus("Виділи посилання та скопіюй його вручну");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submissionLock.current) return;
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Вкажи ім’я";
    if (!form.surname.trim()) nextErrors.surname = "Вкажи прізвище";
    const digits = form.phone.replace(/\D/g, "");
    if (!(digits.length === 10 || (digits.length === 12 && digits.startsWith("380")))) nextErrors.phone = "Вкажи український номер: +380 або 0…";
    if (form.comment.length > 200) nextErrors.comment = "Максимум 200 символів";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (!activeTime || !slots.includes(activeTime)) {
      setStep(0); setActiveTime(null); setSubmitError("Обраний час уже недоступний. Обери інший."); return;
    }
    submissionLock.current = true; setSubmitting(true); setSubmitError("");
    try {
      const res = await fetch(buildApiUrl("/api/requests"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trainer, date: bookingDate, time: activeTime, ...Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()])) }),
      });
      const result = await res.json();
      if (!res.ok) {
        if (res.status === 400 && result.typeError === 101) {
          setStep(0); setActiveTime(null); refreshTime();
          throw new Error("Цей час уже зайнятий. Обери інший час або іншу дату.");
        }
        throw new Error(result.error || "Не вдалося надіслати заявку. Спробуй ще раз.");
      }
      setCancelLink(result.cancelToken ? `${window.location.origin}/cancel/${result.cancelToken}` : "");
      // Storage can be blocked or contain old malformed data; the booking has
      // already succeeded and must never appear to have failed because of it.
      try {
        const parsed = JSON.parse(localStorage.getItem("kokorin_bookings") || "[]");
        const stored = Array.isArray(parsed) ? parsed : [];
        stored.push({ cancelToken: result.cancelToken, trainer: result.trainer, trainerLabel: trainerName, date: result.date, time: result.time, name: result.name, surname: result.surname });
        localStorage.setItem("kokorin_bookings", JSON.stringify(stored));
      } catch {}
      setStep(2);
      onBooked?.();
    } catch (error) {
      setSubmitError(error.message || "Перевір з’єднання та спробуй ще раз.");
    } finally {
      submissionLock.current = false; setSubmitting(false);
    }
  };

  return (
    <>
      <button className="studio-primary" onClick={handleOpen} disabled={!selectedDate}>Обрати час заняття <span aria-hidden="true">↗</span></button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="booking-dialog-title" maxWidth={false}
        slotProps={{ paper: { className: "booking-dialog" }, backdrop: { className: "booking-backdrop" } }}>
        <div className="booking-dialog-header"><span className="dialog-brand">dance<span>sfera</span></span><button className="dialog-close" onClick={handleClose} disabled={submitting} aria-label="Закрити вікно">×</button></div>
        <div className="booking-dialog-body">
          {step < 2 ? <>
            <ol className="booking-progress" aria-label="Кроки запису"><li className={step === 0 ? "is-active" : "is-done"}><span>{step === 0 ? "1" : "✓"}</span> Час заняття</li><li className={step === 1 ? "is-active" : ""}><span>2</span> Твої дані</li></ol>
            <p className="studio-eyebrow">ІНДИВІДУАЛЬНЕ ЗАНЯТТЯ</p>
            <h2 id="booking-dialog-title">{step === 0 ? "Обери зручний час" : "Познайомимось ближче"}</h2>
            <p className="dialog-description">{step === 0 ? "Залиши трохи часу для того, що надихає." : "Залиш контакти для зв’язку щодо заняття."}</p>
            <div className="booking-summary"><img src={trainer === "KOKORIN" ? "/img/IMG_3668.JPG" : "/img/IMG_5596.JPG"} alt="" /><div><strong>{trainerName}</strong><span>Твій тренер</span></div><div className="booking-summary-date"><strong>{formatBookingDate(bookingDate)}</strong><span>{activeTime || "За київським часом"}</span></div></div>
          </> : <>
            <div className="booking-success-icon" aria-hidden="true">✓</div><p className="studio-eyebrow">ДО ЗУСТРІЧІ У DANCE SFERA</p><h2 id="booking-dialog-title">Заявку надіслано!</h2><p className="dialog-description">Тренер отримає твій запис і зможе його підтвердити.</p>
            <div className="booking-success-details"><span>{trainerName}</span><strong>{formatBookingDate(bookingDate)} · {activeTime}</strong></div>
            {cancelLink && <><label className="cancel-link-label" htmlFor="booking-cancel-link">Збережи посилання для скасування запису</label><div className="cancel-link-box"><input id="booking-cancel-link" value={cancelLink} readOnly onClick={(e) => e.target.select()} /><button onClick={copyCancelLink}>Копіювати</button></div><p className="copy-status" role="status">{copyStatus}</p></>}
            <button className="studio-primary" onClick={handleClose}>Готово <span aria-hidden="true">✓</span></button>
          </>}
          {submitError && <p className="booking-error" role="alert">{submitError}</p>}
          {step === 0 && <>
            <fieldset className="booking-time-fieldset"><legend>Доступні години</legend><div className="booking-time-options">{slots.map((time) => <label key={time} className={`booking-time-option${activeTime === time ? " is-selected" : ""}`}><input type="radio" name="booking-time" value={time} checked={activeTime === time} onChange={() => setActiveTime(time)} /><span>{time}</span></label>)}</div></fieldset>
            {!slots.length && <p role="status">На цю дату вільного часу вже немає. Закрий вікно та обери іншу дату.</p>}
            <button className="studio-primary" disabled={!activeTime || !slots.includes(activeTime)} onClick={() => setStep(1)}>Продовжити <span aria-hidden="true">→</span></button>
            <p className="dialog-footnote">Наступний крок — твої контактні дані</p>
          </>}
          {step === 1 && <form onSubmit={handleSubmit} noValidate>
            <div className="booking-form-grid">{[
              { key: "name", label: "Ім’я", placeholder: "Твоє ім’я", autoComplete: "given-name" },
              { key: "surname", label: "Прізвище", placeholder: "Твоє прізвище", autoComplete: "family-name" },
              { key: "phone", label: "Телефон", placeholder: "+380 __ ___ __ __", autoComplete: "tel" },
              { key: "comment", label: "Коментар", placeholder: "Щось, що варто знати тренеру…" },
            ].map(({ key, label, placeholder, autoComplete }) => <div className={`booking-field booking-field-${key}`} key={key}>
              <label htmlFor={`booking-${key}`}>{label} {key === "comment" ? <span>необов’язково</span> : <span aria-hidden="true">*</span>}</label>
              {key === "comment" ? <textarea id={`booking-${key}`} rows={2} maxLength={200} placeholder={placeholder} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} disabled={submitting} /> :
                <input id={`booking-${key}`} type={key === "phone" ? "tel" : "text"} required autoComplete={autoComplete} placeholder={placeholder} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} disabled={submitting} aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `error-${key}` : undefined} />}
              {errors[key] && <span className="field-error" id={`error-${key}`} role="alert">{errors[key]}</span>}
            </div>)}</div>
            <div className="booking-form-actions"><button type="button" className="studio-secondary" onClick={() => setStep(0)} disabled={submitting}>← Назад</button><button className="studio-primary" type="submit" disabled={submitting}>{submitting ? "Надсилаємо…" : "Підтвердити запис"}<span aria-hidden="true">↗</span></button></div>
            <p className="dialog-footnote">Твої контакти потрібні для організації заняття.</p>
          </form>}
        </div>
      </Dialog>
    </>
  );
}
